import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { callFunction } from '@/lib/api';
import type { Database } from '@/types/database.generated';
import {
  consentRecordsQueryKey,
  patientConsentCurrentQueryKey,
  type ConsentDecision,
  type ConsentRecordRow,
  type ConsentTypeKey,
} from '@/hooks/useConsentRecords';

type PublicSchema = Database['public'];

export type ConsentRequestRow = PublicSchema['Tables']['consent_requests']['Row'];
export type ConsentRequestItemRow = PublicSchema['Tables']['consent_request_items']['Row'];
export type ConsentRequestChannel = PublicSchema['Enums']['consent_request_channel'];
export type ConsentRequestWithItems = ConsentRequestRow & {
  consent_request_items: ConsentRequestItemRow[];
};
export type PortalConsentInboxRow = PublicSchema['Functions']['portal_consent_inbox']['Returns'][number];
export type PortalConsentDocumentPromptRow =
  PublicSchema['Functions']['portal_consent_document_prompts']['Returns'][number];

export type ConsentRequestNotificationStatus = 'sent' | 'failed' | 'skipped';

export interface SendConsentRequestOutput {
  request_id: string;
  channel: ConsentRequestChannel;
  status: ConsentRequestNotificationStatus;
  detail: string | null;
}

export interface CreateConsentRequestResult {
  request: ConsentRequestRow;
  notification: SendConsentRequestOutput | null;
}

export const patientConsentRequestsQueryKey = (patientId: string | null) =>
  ['consent-requests', patientId] as const;
export const portalConsentInboxQueryKey = (clinicId?: string | null) =>
  clinicId === undefined
    ? (['portal-consent-inbox'] as const)
    : (['portal-consent-inbox', clinicId] as const);

export const portalConsentDocumentPromptsQueryKey = (clinicId?: string | null) =>
  clinicId === undefined
    ? (['portal-consent-document-prompts'] as const)
    : (['portal-consent-document-prompts', clinicId] as const);

export function usePatientConsentRequests(
  patientId: string | null | undefined,
  clinicId?: string | null,
) {
  return useQuery({
    queryKey: clinicId
      ? ([...patientConsentRequestsQueryKey(patientId ?? null), clinicId] as const)
      : patientConsentRequestsQueryKey(patientId ?? null),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<ConsentRequestWithItems[]> => {
      if (!patientId) return [];
      let requestsQuery = supabase
        .from('consent_requests')
        .select('*, consent_request_items(*)')
        .eq('patient_id', patientId);
      if (clinicId) requestsQuery = requestsQuery.eq('clinic_id', clinicId);
      const { data, error } = await requestsQuery.order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as ConsentRequestWithItems[];
    },
    staleTime: 15_000,
  });
}

/**
 * Creates the request, then asks send-consent-request to notify the patient.
 * A failed notification does not undo the request; the outcome is stored on it.
 */
export function useCreateConsentRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      patientId: string;
      textVersionIds: string[];
      expiresAt?: string;
    }): Promise<CreateConsentRequestResult> => {
      const { data, error } = await supabase.rpc('create_consent_request', {
        p_patient_id: args.patientId,
        p_text_version_ids: args.textVersionIds,
        ...(args.expiresAt ? { p_expires_at: args.expiresAt } : {}),
      });
      if (error) throw error;
      let notification: SendConsentRequestOutput | null = null;
      try {
        notification = await callFunction<{ request_id: string }, SendConsentRequestOutput>(
          'send-consent-request',
          { request_id: data.id },
        );
      } catch (err) {
        console.error('send-consent-request failed', err);
      }
      return { request: data, notification };
    },
    onSettled: (_result, _error, args) => {
      queryClient.invalidateQueries({ queryKey: patientConsentRequestsQueryKey(args.patientId) });
    },
  });
}

export function useCancelConsentRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string): Promise<ConsentRequestRow> => {
      const { data, error } = await supabase.rpc('cancel_consent_request', { p_request_id: requestId });
      if (error) throw error;
      return data;
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: patientConsentRequestsQueryKey(row.patient_id) });
    },
  });
}

export function usePortalConsentInbox(enabled = true, clinicId?: string | null) {
  const scoped = clinicId !== undefined;
  return useQuery({
    queryKey: portalConsentInboxQueryKey(scoped ? clinicId : undefined),
    enabled: enabled && (!scoped || Boolean(clinicId)),
    queryFn: async (): Promise<PortalConsentInboxRow[]> => {
      const { data, error } = await supabase.rpc(
        'portal_consent_inbox',
        clinicId ? { p_clinic_id: clinicId } : {},
      );
      if (error) throw error;
      const rows = data ?? [];
      return clinicId ? rows.filter((row) => row.clinic_id === clinicId) : rows;
    },
    staleTime: 30_000,
  });
}

export function usePortalConsentDocumentPrompts(enabled = true, clinicId?: string | null) {
  const scoped = clinicId !== undefined;
  return useQuery({
    queryKey: portalConsentDocumentPromptsQueryKey(scoped ? clinicId : undefined),
    enabled: enabled && (!scoped || Boolean(clinicId)),
    queryFn: async (): Promise<PortalConsentDocumentPromptRow[]> => {
      const { data, error } = await supabase.rpc(
        'portal_consent_document_prompts',
        clinicId ? { p_clinic_id: clinicId } : {},
      );
      if (error) throw error;
      const rows = data ?? [];
      return clinicId ? rows.filter((row) => row.clinic_id === clinicId) : rows;
    },
    staleTime: 60_000,
  });
}

function invalidatePortalConsent(queryClient: ReturnType<typeof useQueryClient>, record: ConsentRecordRow) {
  queryClient.invalidateQueries({ queryKey: ['portal-consent-inbox'] });
  queryClient.invalidateQueries({ queryKey: ['portal-consent-document-prompts'] });
  queryClient.invalidateQueries({ queryKey: consentRecordsQueryKey(record.patient_id) });
  queryClient.invalidateQueries({ queryKey: patientConsentCurrentQueryKey(record.patient_id) });
}

export function useAnswerConsentRequestItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      itemId: string;
      decision: Extract<ConsentDecision, 'given' | 'refused'>;
      relationId: string | null;
    }): Promise<ConsentRecordRow> => {
      const { data, error } = await supabase.rpc('answer_consent_request_item', {
        p_item_id: args.itemId,
        p_decision: args.decision,
        ...(args.relationId ? { p_given_by_relation_id: args.relationId } : {}),
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (record) => invalidatePortalConsent(queryClient, record),
  });
}

/** Portal user reads the privacy notice / accepts the portal terms for their own patient row. */
export function useAcknowledgePortalConsentDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      patientId: string;
      consentType: Extract<ConsentTypeKey, 'privacy_notice' | 'portal_terms'>;
      textVersionId: string;
    }): Promise<ConsentRecordRow> => {
      const { data, error } = await supabase.rpc('record_consent', {
        p_patient_id: args.patientId,
        p_consent_type: args.consentType,
        p_decision: 'given',
        p_text_version_id: args.textVersionId,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (record) => invalidatePortalConsent(queryClient, record),
  });
}
